import {
  cacheKeys,
  FIND_ROUTINE_ASSIGNMENTS,
  useCurrentWorkspace,
  useCustomMutation,
  useCustomQuery,
} from '@gripp/shared-logic';
import {
  Colors,
  SaveFormTop,
  TopModalBar,
  useGetDimensions,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { EditRoutineAssetsList } from './editRoutineAssetsList';
import { RoutineAssetSearchInput } from './routineAssetSearchInput';

type Props = {
  routineId: string;
  title: string;
  showCheckboxColumn: boolean;
  onComplete: () => void;
};

export const RoutineAssetsForm = ({
  routineId,
  title,
  showCheckboxColumn,
  onComplete,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { dimensions, onLayout } = useGetDimensions();
  const height = dimensions?.height ?? 0;
  const tableResultsBlockHeight = 50;
  const listMaxHeight = height - tableResultsBlockHeight;

  const [originalAssetAssignments, setOriginalAssetAssignments] = useState<
    string[] | undefined
  >();
  const [assetAssignments, setAssetAssignments] = useState<
    string[] | undefined
  >(undefined);

  const [keyword, setKeyword] = useState('');
  const [tabIndex, setTabIndex] = useState(0);

  const { data: assignments, isPending: isAssignmentsLoading } = useCustomQuery(
    {
      query: FIND_ROUTINE_ASSIGNMENTS,
      cacheKey: [],
      variables: {
        routineId: routineId,
      },
    }
  );

  const { mutateAsync: assignAssetsToRoutine, isPending: isAssetsLoading } =
    useCustomMutation({
      query: ASSIGN_ASSETS_TO_ROUTINE,
    });

  useEffect(() => {
    if (assignments && !isAssignmentsLoading) {
      const assetAssignments =
        assignments.routine.findRoutineAssignments.assets;
      setOriginalAssetAssignments(assetAssignments);
      setAssetAssignments(assetAssignments);
    }
  }, [
    assignments?.routine?.findRoutineAssignments?.assets,
    isAssignmentsLoading,
  ]);

  const handleAssetsSubmit = async () => {
    const input = {
      assetIds: assetAssignments,
      routineId,
      workspaceId: currentWorkspace.id,
    };

    await assignAssetsToRoutine(
      { input: input },
      {
        onSettled: async () => {
          await queryClient.invalidateQueries({
            queryKey: cacheKeys('routine').all,
          });
        },
      }
    );

    onComplete();
  };
  const currentWorkspace = useCurrentWorkspace();

  const isDirty = () => {
    return (
      originalAssetAssignments &&
      assetAssignments &&
      !(
        originalAssetAssignments.length === assetAssignments.length &&
        originalAssetAssignments.every((item) =>
          assetAssignments.includes(item)
        ) &&
        assetAssignments.every((item) =>
          originalAssetAssignments.includes(item)
        )
      )
    );
  };

  return (
    <SaveFormTop
      onSubmit={handleAssetsSubmit}
      title={title}
      topBar={TopModalBar}
      onCancel={onComplete}
      isDirty={isDirty()}
    >
      <View style={styles.container}>
        <RoutineAssetSearchInput
          keyword={keyword}
          onChange={setKeyword}
          placeholder="assetSearchPlaceholder"
        />

        <View style={[styles.assignmentContainer]} onLayout={onLayout}>
          <Tabs
            selectedIndex={tabIndex}
            onSelect={(index) => {
              setTabIndex(index);
            }}
          >
            <TabList>
              <Tab>{t('groups.assignments.assets')}</Tab>
            </TabList>

            <View style={styles.listContainer}>
              <TabPanel>
                {assetAssignments && currentWorkspace?.id && (
                  <EditRoutineAssetsList
                    workspaceId={currentWorkspace.id}
                    routineId={routineId}
                    maxHeight={listMaxHeight}
                    keyword={keyword}
                    assignments={assetAssignments}
                    updateAssignments={setAssetAssignments}
                  />
                )}
              </TabPanel>
            </View>
          </Tabs>
        </View>
      </View>
    </SaveFormTop>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 20,
  },
  assignmentContainer: {
    flex: 1,
    marginTop: 20,
  },
  listContainer: {
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
    borderBottomLeftRadius: 12,
    overflow: 'hidden',
  },
  inputStyle: {
    backgroundColor: Colors.white,
    borderColor: Colors.grayDivider,
    borderWidth: 1,
    borderRadius: 8,
  },
});

const ASSIGN_ASSETS_TO_ROUTINE = `
  mutation Routine($input: AssignToRoutineInput!) {
    routine {
      assignToRoutine(input: $input) {
        message
        success
      }
    }
  }
`;
